import React, { useMemo, useState } from "react";
import { Box, Button, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import {
  tokens,
  StatusPill,
  CodeChip,
  PatientStrip,
  ClosedLoopTracker,
  SectionPanel,
  ClinicalEmptyState,
} from "../../shared/clinical-design";
import {
  HEALTH_STATUS_META,
  TIMEFRAME_META,
  OUTREACH_CHANNELS,
  OUTREACH_OUTCOMES,
  RESPONSE_OUTCOMES,
} from "../common/constants";

const LABEL_SX = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: tokens.ink3,
};
const VALUE_SX = {
  fontFamily: tokens.fontMono,
  fontVariantNumeric: "tabular-nums",
  fontSize: 14,
  color: tokens.ink,
};

function Field({ label, children }) {
  return (
    <Box>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography component="div" sx={VALUE_SX}>{children ?? "—"}</Typography>
    </Box>
  );
}

/**
 * Detalle del miembro a contactar: contactabilidad, contexto clínico y log de
 * outreach (estado local: registrar intento + outcome) con closed-loop tracker.
 */
export default function OutreachDetailPanel({ row, locationLabel }) {
  const [log, setLog] = useState([]);
  const [channel, setChannel] = useState(OUTREACH_CHANNELS[0]);
  const [outcome, setOutcome] = useState(OUTREACH_OUTCOMES[0]);

  const loopSteps = useMemo(() => {
    const attempted = log.length > 0 || (row?.priorAttempts ?? 0) > 0;
    const responded = log.some((e) => RESPONSE_OUTCOMES.includes(e.outcome));
    const appointment = log.some((e) => e.outcome === "Reached — appointment requested");
    const declined = log.some((e) => e.outcome === "Reached — declined visit");
    return [
      { label: "Member identified", state: "done" },
      { label: "Outreach attempted", state: attempted ? "done" : "active" },
      {
        label: "Member responded",
        state: responded ? "done" : attempted ? "active" : "pending",
      },
      {
        label: "Appointment scheduled",
        state: appointment ? "done" : declined ? "blocked" : responded ? "active" : "pending",
      },
    ];
  }, [log, row]);

  if (!row) {
    return (
      <SectionPanel title="Member detail">
        <ClinicalEmptyState message="Select a member from the outreach queue to start contact." />
      </SectionPanel>
    );
  }

  const tfTone = TIMEFRAME_META[row.timeSinceVisit]?.tone || "muted";
  const hsTone = HEALTH_STATUS_META[row.healthStatus]?.tone || "muted";

  const logAttempt = () => {
    setLog((prev) => [
      {
        at: new Date().toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" }),
        channel,
        outcome,
      },
      ...prev,
    ]);
  };

  return (
    <Stack spacing={1.5}>
      <PatientStrip
        patient={{ name: row.name, cipId: row.cipId, location: locationLabel }}
        actions={
          <Stack direction="row" spacing={1}>
            <StatusPill tone={hsTone} label={row.healthStatus || "Unknown"} dense />
            <StatusPill tone={tfTone} label={`No visit ${row.timeSinceVisit}`} dense />
          </Stack>
        }
      />

      <SectionPanel title="Outreach closed-loop">
        <ClosedLoopTracker steps={loopSteps} />
      </SectionPanel>

      <SectionPanel title="Contactability">
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Field label="Phone">{row.phone || "—"}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Field label="Mobile">{row.mobile || "—"}</Field>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Field label="Email">
              <Box component="span" sx={{ ...VALUE_SX, fontSize: 12.5, wordBreak: "break-all" }}>
                {row.email || "—"}
              </Box>
            </Field>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Field label="Preferred channel">
              {row.preferredChannel ? (
                <CodeChip code={row.preferredChannel} tone="info" />
              ) : (
                <StatusPill tone="crit" label="Not contactable" dense />
              )}
            </Field>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Field label="Conditions">
              <Box component="span" sx={{ fontFamily: tokens.fontUI, fontSize: 13, color: tokens.ink2 }}>
                {row.conditions || "—"}
              </Box>
            </Field>
          </Grid>
        </Grid>
      </SectionPanel>

      <SectionPanel
        title="Outreach log"
        actions={
          <Typography sx={{ fontSize: 11.5, color: tokens.ink3 }}>
            {row.priorAttempts + log.length} attempt(s) total
          </Typography>
        }
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }} alignItems="center">
          <Select
            size="small"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            sx={{ fontSize: 13, minWidth: 100, bgcolor: tokens.surface }}
          >
            {OUTREACH_CHANNELS.map((c) => (
              <MenuItem key={c} value={c} sx={{ fontSize: 13 }}>{c}</MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            sx={{ fontSize: 13, minWidth: 240, bgcolor: tokens.surface }}
          >
            {OUTREACH_OUTCOMES.map((o) => (
              <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>{o}</MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={logAttempt}
            sx={{
              bgcolor: tokens.primary,
              "&:hover": { bgcolor: tokens.primary700 },
              textTransform: "none",
              fontSize: 13,
            }}
          >
            Log attempt
          </Button>
        </Stack>

        {log.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: tokens.ink3, mt: 1.5 }}>
            No attempts logged in this session.
            {row.priorAttempts > 0 &&
              ` ${row.priorAttempts} prior attempt(s) on record (simulated).`}
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            {log.map((entry, i) => (
              <Stack
                key={`${entry.at}-${i}`}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  px: 1.25,
                  py: 0.5,
                  bgcolor: tokens.surface2,
                  border: `1px solid ${tokens.line}`,
                  borderRadius: "4px",
                }}
              >
                <Typography sx={{ ...VALUE_SX, fontSize: 12, color: tokens.ink3 }}>
                  {entry.at}
                </Typography>
                <CodeChip code={entry.channel} />
                <Typography sx={{ fontSize: 13, color: tokens.ink2 }}>{entry.outcome}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
        <Typography sx={{ fontSize: 11.5, color: tokens.ink3, mt: 1 }}>
          Log is local-only in this preview — it will persist to
          outreach.TAB_OUTREACH_LOG once the campaign backend is available.
        </Typography>
      </SectionPanel>
    </Stack>
  );
}
